import {defineStore} from 'pinia'
import {ProjectApi} from "@/api/project.apis";
import {BoardsNs, CommonNs, IntegrationNs, ProjectNs} from "@shared/types";
import {AiApi} from "@/api/ai.apis";
import {getLocalStorage, setLocalStorage} from "@shared/helpers";
import {Subject} from "rxjs";
import {IntegrationApi} from "@/api";


export const aiActionSubject = new Subject();
export const useSelectedProjectStore = defineStore("selectedProject", {
  state: () => ({
    selectedProject: {} as ProjectNs.Project,
    recentlyViewed: [] as BoardsNs.RecentlyViewed[],
    aiBalanceInfo: {} as BoardsNs.AIBalanceInfo
  }),
  getters: {
    getSelectedProject: (state) => state.selectedProject,
    getSelectedProjectId: (state) => state.selectedProject.id,
    getSelectedProjectName: (state) => state.selectedProject.name,
    getSelectedProjectRole: (state) => state.selectedProject.projectRole,
    getProjectIntegrations: (state) => state.selectedProject.projectIntegrationList,
    getWorkspaceIntegrations: (state) => state.selectedProject.workspaceIntegrationList,
    getIsSelectedProjectSearchable: (state) => state.selectedProject.searchable || false,

    getRecentlyViewedByCurrentProject: (state) => {
      let rViewed = state.recentlyViewed;
      if (!rViewed || rViewed.length === 0) {
        rViewed = getLocalStorage('recentlyViewed');
      }
      if (rViewed && rViewed.length > 0 && state.selectedProject.id) {
        return rViewed.find((r) => r.projectId === state.selectedProject.id);
      }
      return {} as BoardsNs.RecentlyViewed;
    },

    getAIBalanceInfo: (state): BoardsNs.AIBalanceInfo => state.aiBalanceInfo,

    isApplicationDetailsDefined: (state) => {
      return state.selectedProject.platformType ||
          state.selectedProject.platform ||
          state.selectedProject.branchType ||
          state.selectedProject.branch ||
          state.selectedProject.targetGroup;
    },

    isJiraManualSyncActive: (state) => {
      const integrationList = state.selectedProject.projectIntegrationList
      if (integrationList?.length > 0) {
        const pIntegration = integrationList.find((integration: any) => integration.integrationType === 'JIRA')
        if (pIntegration) {
          if (pIntegration.synchronizationType === ProjectNs.JiraSyncMode.MANUAL_SYNC) {
            return true;
          }
        }
      }
      return false;
    },

    getJiraSyncMode: (state) => {
      const integrationList = state.selectedProject.projectIntegrationList
      if (integrationList?.length > 0) {
        const pIntegration = integrationList.find((integration: any) => integration.integrationType === 'JIRA')
        if (pIntegration) {
          return pIntegration.synchronizationType;
        }
      }
      return ProjectNs.JiraSyncMode.ONLY_IMPORT;
    },

    isAzureManualSyncActive: (state) => {
      const integrationList = state.selectedProject.projectIntegrationList
      if (integrationList?.length > 0) {
        const pIntegration = integrationList.find((integration: any) => integration.integrationType === 'AZURE')
        if (pIntegration) {
          if (pIntegration.synchronizationType === ProjectNs.JiraSyncMode.MANUAL_SYNC) {
            return true;
          }
        }
      }
      return false;
    },

    getAzureSyncMode: (state) => {
      const integrationList = state.selectedProject.projectIntegrationList
      if (integrationList?.length > 0) {
        const pIntegration = integrationList.find(
            (integration: any) => integration.integrationType === 'AZURE'
                || integration.integrationType === 'AZURE_BOARDS')
        if (pIntegration) {
          return pIntegration.synchronizationType;
        }
      }
      return ProjectNs.JiraSyncMode.ONLY_IMPORT;
    },

    getIntegrationProjectKey: (state) => (type: string): string | undefined => {
      return state.selectedProject.projectIntegrationList
        ?.find((i: ProjectNs.ProjectIntegration) => i.integrationType === type)
        ?.intProjectKey
    },
  },
  actions: {
    async loadProject(projectId: string): Promise<ProjectNs.Project> {
      return ProjectApi.getProject(projectId).then((project) => {
        this.selectedProject = project;
        return project;
      })
    },

    async updateProject(payload: ProjectNs.UpdateProjectRequest): Promise<ProjectNs.Project> {
      return ProjectApi.updateProject(payload)
      .then((project) => {
        this.selectedProject = project;
        return project;
      })
    },

    async generateWithAI(payload: BoardsNs.AISearchRequest
    ): Promise<BoardsNs.AISearchResult> {
      return AiApi.generateWithAI(payload)
      .then((searchResult) => {
        this.aiBalanceInfo = searchResult.balanceInfo;
        return searchResult;
      })
    },

    async publishProject(payload: ProjectNs.PublishProjectRequest
    ): Promise<ProjectNs.PublishProjectResponse> {
      return ProjectApi.publishProject(payload)
      .then((response) => {

        return response;
      })
    },

    async unpublishProject(payload: ProjectNs.GenericProjectRequest
    ): Promise<CommonNs.AnyResponse> {
      return ProjectApi.unpublishProject(payload)
      .then((response) => {
        return response;
      })
    },

    async loadProjectContents(payload: ProjectNs.GenericProjectRequest
    ): Promise<ProjectNs.ProjectContents> {
      return ProjectApi.getProjectContents(payload);
    },

    async loadPublishedProject(payload: ProjectNs.GenericProjectRequest
    ): Promise<ProjectNs.PublishProjectResponse> {
      return ProjectApi.loadPublishedProject(payload)
      .then((response) => {
        return response;
      })
    },

    addItemToRecentlyViewed(payload: { type: 'FEATURE' | 'SCREEN', item: any }): void {
      let rViewed = this.recentlyViewed;
      if (!rViewed || rViewed.length === 0) {
        rViewed = getLocalStorage('recentlyViewed');
        if (!rViewed) {
          rViewed = [] as BoardsNs.RecentlyViewed[];
        }
      }

      let projectViewed;
      if (rViewed && rViewed.length > 0 && this.selectedProject.id) {
        projectViewed = rViewed.find((r) => r.projectId === this.selectedProject.id);
      }

      if (!projectViewed) {
        projectViewed = {
          projectId: this.selectedProject.id,
          features: [] as BoardsNs.RecentlyViewedItem[],
          screens: [] as BoardsNs.RecentlyViewedItem[]
        } as BoardsNs.RecentlyViewed;
        rViewed.push(projectViewed);
      }

      if (payload.type === 'FEATURE') {
        const index = projectViewed.features.findIndex((r: BoardsNs.RecentlyViewedItem) => r.id === payload.item.id);
        if (index > -1) {
          projectViewed.features.splice(index, 1);
        }
        projectViewed.features.unshift({
          id: payload.item.id,
          type: payload.item.featureType,
          summary: payload.item.summary,
          color: payload.item.properties?.color
        });
        projectViewed.features = projectViewed.features.splice(0, 5);
      } else {
        const index = projectViewed.screens.findIndex((r: BoardsNs.RecentlyViewedItem) => r.id === payload.item.id);
        if (index > -1) {
          projectViewed.screens.splice(index, 1);
        }
        projectViewed.screens.unshift({
          id: payload.item.id,
          type: payload.item.screenType,
          summary: payload.item.summary,
          color: payload.item.color
        });
        projectViewed.screens = projectViewed.screens.splice(0, 5);
      }

      this.recentlyViewed = rViewed;
      setLocalStorage('recentlyViewed', rViewed);
    },

    async moveProjectToTrash(payload: ProjectNs.GenericProjectRequest
    ): Promise<CommonNs.AnyResponse> {
      return ProjectApi.deleteProject(payload)
    },

    async makeProjectSearchable(payload: ProjectNs.GenericProjectRequest
    ): Promise<CommonNs.AnyResponse> {
      return ProjectApi.makeProjectSearchable(payload)
      .then((response) => {
        this.selectedProject.searchable = true;
        return response;
      })
    },

    async makeProjectUnsearchable(payload: ProjectNs.GenericProjectRequest
    ): Promise<CommonNs.AnyResponse> {
      return ProjectApi.makeProjectUnsearchable(payload)
      .then((response) => {
        this.selectedProject.searchable = false;
        return response;
      })
    },

    async loadLibraryProjectsInWorkspace(
        payload: ProjectNs.GetProjectsInWorkspaceRequest
    ): Promise<ProjectNs.Project[]> {
      return ProjectApi.getLibraryProjectsInWorkspace(payload)
      .then((allProjects) => allProjects)
    },

    async changeProjectSyncType(
        payload: IntegrationNs.ChangeSyncTypeRequest
    ): Promise<CommonNs.AnyResponse> {
      return IntegrationApi.changeSyncType(payload)
      .then(() => {
        this.loadProject(payload.projectId).then((response) => response)
      })
    }

  }

})
